import React from 'react';
import { Link } from 'react-router-dom';
import './Kitchen&WardrobeFitting.css';
import QuoteForm from '../../components/QuoteForm';
import SEO from '../../components/SEO';

const KitchenWardrobeFittingPage = () => {

  const pricingGuide = [
    {
      title: 'Full Installation (Materials + Labour)',
      items: [
        'Kitchen cabinets: from Ksh 450,000 (varies with size and layout)',
        'Wardrobes: from Ksh 100,000 (depending on size and design)'
      ]
    },
    {
      title: 'Labour Only (Client Supplies Materials)',
      items: [
        'Kitchen & wardrobe installation: 30% of total material cost'
      ]
    }
  ];

  const services = [
    {
      title: 'Kitchen Fitting',
      description: 'Custom kitchen cabinets, modular kitchen design, countertop installation, storage optimization, and lighting integration.',
      icon: '🍳'
    },
    {
      title: 'Wardrobe Fitting',
      description: 'Sliding wardrobes, walk-in closets, built-in wardrobes, custom shelving systems, and soft-close fittings.',
      icon: '👔'
    },
    {
      title: 'Custom Cabinetry',
      description: 'Bespoke cabinets tailored to your space, style, and storage needs with premium materials and finishes.',
      icon: '🗄️'
    },
    {
      title: 'Storage Solutions',
      description: 'Maximize your space with intelligent storage designs that combine functionality with modern aesthetics.',
      icon: '📦'
    }
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Consultation & Space Assessment',
      description: 'We visit your space, understand your requirements, and assess the available area for optimal design.'
    },
    {
      step: '2',
      title: '3D Design & Planning',
      description: 'Our designers create detailed 3D visuals so you can see the final result before installation begins.'
    },
    {
      step: '3',
      title: 'Material Selection',
      description: 'Choose from a wide range of premium materials, finishes, and hardware to match your style and budget.'
    },
    {
      step: '4',
      title: 'Fabrication',
      description: 'Your custom kitchen and wardrobe components are precision-manufactured using advanced techniques.'
    },
    {
      step: '5',
      title: 'Professional Installation',
      description: 'Our expert team ensures seamless installation with attention to every detail and finish.'
    },
    {
      step: '6',
      title: 'Final Inspection & Handover',
      description: 'We conduct thorough quality checks and hand over your beautiful new kitchen or wardrobe.'
    }
  ];

  const materials = [
    { name: 'MDF & HDF Boards', description: 'Durable and versatile for cabinet construction' },
    { name: 'Plywood Cabinets', description: 'Strong and moisture-resistant for kitchen use' },
    { name: 'Laminate Finishes', description: 'Wide range of colors and textures' },
    { name: 'High-Gloss & Matte', description: 'Modern finishes for contemporary spaces' },
    { name: 'Soft-Close Hinges & Rails', description: 'Premium hardware for smooth operation' }
  ];

  const whyChooseUs = [
    'Experienced interior designers and craftsmen',
    'Custom solutions for every space and budget',
    'Affordable pricing with transparent quotes',
    'High-quality workmanship and materials',
    'On-time project delivery guaranteed',
    'After-sales support and warranty'
  ];

  const faqs = [
    {
      question: 'What is the cost of kitchen fitting services?',
      answer: 'The cost varies depending on the size, materials, and complexity of your kitchen. We provide free consultations and detailed quotes tailored to your specific requirements. Contact us for a personalized estimate.'
    },
    {
      question: 'How long does wardrobe installation take?',
      answer: 'A standard wardrobe installation typically takes 2-5 days, while custom walk-in closets may take 1-2 weeks. The timeline depends on the complexity and size of the project.'
    },
    {
      question: 'What materials are best for kitchen cabinets?',
      answer: 'Plywood is excellent for moisture resistance in kitchens, while MDF offers great versatility for painted finishes. We also offer high-gloss laminates and solid wood options based on your preferences and budget.'
    },
    {
      question: 'Do you offer custom wardrobe designs?',
      answer: 'Yes! We specialize in custom wardrobe solutions including sliding doors, walk-in closets, built-in wardrobes, and bespoke shelving systems tailored to your space and storage needs.'
    },
    {
      question: 'Can I get a 3D kitchen design before installation?',
      answer: 'Absolutely! We provide detailed 3D designs and visualizations so you can see exactly how your kitchen or wardrobe will look before we begin fabrication and installation.'
    }
  ];

  const testimonials = [
    {
      name: 'Michael T.',
      location: 'Nairobi',
      text: 'The kitchen installation exceeded our expectations. The team was professional, and the quality of work is outstanding. Highly recommend!',
      rating: 5
    },
    {
      name: 'Grace W.',
      location: 'Mombasa',
      text: 'Our custom wardrobe fits perfectly in our bedroom. The sliding doors are smooth, and the storage space is incredible. Great job!',
      rating: 5
    },
    {
      name: 'David K.',
      location: 'Kisumu',
      text: 'From design to installation, the entire process was seamless. The 3D visualization helped us make confident decisions. Very satisfied!',
      rating: 5
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Kitchen & Wardrobe Fitting",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Techie Interiors",
      "telephone": "+254748600208",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Nairobi",
        "addressCountry": "KE"
      }
    },
    "areaServed": "Kenya",
    "description": "Custom kitchen and wardrobe fitting services in Nairobi Kenya. Professional installation of cabinets, countertops, and storage solutions."
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="kitchen-wardrobe-page">
      {/* SEO Component */}
      <SEO
        title="Kitchen Cabinets & Wardrobe Fitting in Nairobi | Techie Interiors"
        description="Professional kitchen cabinet and wardrobe fitting services in Nairobi, Kenya. Get custom cabinetry, modern finishes, pricing guidance, and expert installation for homes and apartments."
        image="/techLogo.jpeg"
        url="https://techieinteriors.co.ke/kitchen-wardrobe-fitting"
        structuredData={[structuredData, faqSchema]}
      />
      {/* 1. HERO SECTION */}
      <section className="kw-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Custom Kitchen & Wardrobe Fitting Services | Modern, Durable & Stylish Interiors in Nairobi, Kenya.</h1>
          <p>Transform your space with expertly crafted kitchen cabinets and custom wardrobe fittings tailored to your lifestyle, space, and budget.</p>
          <div className="hero-buttons">
            <a href="#contact-form" className="btn btn-primary">Get Free Quote</a>
            <Link to="/project" className="btn btn-secondary">Our Designs</Link>
          </div>
        </div>
      </section>

      {/* 2. QUICK VALUE PROPOSITION */}
      <section className="kw-value-prop">
        <div className="container">
          <div className="value-grid">
            <div className="value-item">
              <div className="value-icon">✔️</div>
              <h3>Custom Designs</h3>
            </div>
            <div className="value-item">
              <div className="value-icon">✔️</div>
              <h3>Premium Materials</h3>
            </div>
            <div className="value-item">
              <div className="value-icon">✔️</div>
              <h3>Space Optimization</h3>
            </div>
            <div className="value-item">
              <div className="value-icon">✔️</div>
              <h3>Professional Installation</h3>
            </div>
          </div>
          <p className="value-microcopy">We deliver functional, stylish, and durable kitchen and wardrobe solutions for homes, apartments, and commercial spaces.</p>
        </div>
      </section>

      {/* 3. SERVICES OVERVIEW */}
      <section className="kw-services">
        <div className="container">
          <h2 className="section-title">Our Kitchen & Wardrobe Fitting Services</h2>
          <p className="services-intro">
            We specialize in complete kitchen and wardrobe fitting solutions, including design, manufacturing, and installation. Whether you're upgrading your kitchen cabinets or installing custom wardrobes, we provide end-to-end interior solutions using high-quality materials and modern techniques.
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. SEO CONTENT BLOCK */}
      <section className="kw-seo-content">
        <div className="container">
          <div className="seo-content">
            <h2 className="section-title-left">Professional Kitchen & Wardrobe Installation for Modern Living</h2>
            <p>
              Our kitchen and wardrobe fitting services are designed to enhance both functionality and aesthetics. We use advanced interior design principles to create kitchens that improve workflow and wardrobes that maximize storage efficiency.
            </p>
            <p>
              From contemporary kitchen cabinets to space-saving wardrobe designs, every project is tailored to meet your needs. Our team ensures precision installation, durable finishes, and seamless integration with your interior space.
            </p>
            <p>
              We work with a wide range of materials including MDF, plywood, laminates, and high-gloss finishes to deliver long-lasting and visually appealing results.
            </p>
          </div>
        </div>
      </section>

      {/* 5. PORTFOLIO / GALLERY SECTION */}
      {/* <section className="kw-portfolio">
        <div className="container">
          <h2 className="section-title">Our Recent Kitchen & Wardrobe Projects</h2>
          <div className="portfolio-grid">
            {portfolioItems.map((item, index) => (
              <div key={index} className="portfolio-card">
                <div className="portfolio-image">{item.image}</div>
                <div className="portfolio-overlay">
                  <h3>{item.title}</h3>
                  <p>{item.category}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* 6. PROCESS SECTION */}
      <section className="kw-process">
        <div className="container">
          <h2 className="section-title">Our Kitchen & Wardrobe Fitting Process</h2>
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. MATERIALS & FINISHES */}
      <section className="kw-materials">
        <div className="container">
          <h2 className="section-title">High-Quality Materials for Kitchen & Wardrobe Fittings</h2>
          <div className="materials-grid">
            {materials.map((material, index) => (
              <div key={index} className="material-card">
                <h3>{material.name}</h3>
                <p>{material.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. WHY CHOOSE US */}
      <section className="kw-why-choose">
        <div className="container">
          <h2 className="section-title">Why Choose Our Kitchen & Wardrobe Fitting Services?</h2>
          <div className="why-choose-grid">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="why-choose-item">
                <div className="check-icon">✓</div>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. GUIDE PRICING */}
      <section className="pricing-guide">
        <div className="container">
          <h2>Guide Pricing</h2>
          <p className="pricing-intro">
            Prices vary depending on design, size, and materials used.
          </p>
          <div className="pricing-grid">
            {pricingGuide.map((section, index) => (
              <div key={index} className="pricing-card">
                <h3>{section.title}</h3>
                <ul>
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pricing-note">
            <p>
              <strong>Note:</strong> Final quotation is provided after site visit and design discussion.
            </p>
          </div>
        </div>
      </section>


      {/* 9. FAQ SECTION */}
      <section className="kw-faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions About Kitchen & Wardrobe Fitting</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. CONTACT FORM */}
      <section className="kw-form-section" id="contact-form">
        <div className="container">
          <h2 className="section-title">Request a Kitchen & Wardrobe Fitting Quote</h2>
          <div className="form-wrapper">
            <div className="contact-info">
              <h3>Get In Touch</h3>
              <p>Have questions about our kitchen and wardrobe fitting services? We're here to help transform your space.</p>
              <div className="info-item">
                <strong>Phone:</strong>
                <span>+254748600208</span>
              </div>
              <div className="info-item">
                <strong>Email:</strong>
                <span>info@techieinteriors.co.ke</span>
              </div>
              <div className="info-item">
                <strong>Location:</strong>
                <span>Nairobi, Kenya</span>
              </div>
              <div className="trust-line">
                <strong>We respond instantly.</strong>
              </div>
            </div>
            <QuoteForm serviceName="Kitchen & Wardrobe Fitting" />
          </div>
        </div>
      </section>

      {/* 12. TESTIMONIALS */}
      <section className="kw-trust">
        <div className="container">
          <h2 className="section-title">Trusted Across Kenya</h2>
          <p className="trust-subtitle">We've delivered quality kitchen and wardrobe solutions to homes and businesses across Kenya</p>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="rating">{'⭐'.repeat(testimonial.rating)}</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 13. SEO FOOTER CONTENT */}
      <section className="kw-seo-footer">
        <div className="container">
          <p>
            We provide kitchen and wardrobe fitting services for residential and commercial properties, delivering modern kitchen cabinets, custom wardrobes, and interior fit-out solutions. Our expertise ensures efficient space utilization, durable materials, and elegant finishes for every project.
          </p>
          <p>
            Need related services? Explore <Link to="/services">all services</Link>,{' '}
            <Link to="/gypsum-ceiling">gypsum ceilings</Link>,{' '}
            <Link to="/tv-gypsum-wall">TV wall units</Link>, and{' '}
            <Link to="/kitchen-cabinets-nairobi">kitchen cabinets in Nairobi</Link>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default KitchenWardrobeFittingPage;
